
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Review } from "@/types/review";
import MapContent from "./map/MapContent";
import { ReviewMapProps } from "./map/MapTypes";
import { config } from "@/config";

const ReviewMap: React.FC<ReviewMapProps> = ({ 
  reviews, 
  height = "500px", 
  searchQuery, 
  clickable = true,
  groupedReviews
}) => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: config.defaultMapCenter.lat, 
    lng: config.defaultMapCenter.lng
  });
  const [mapKey, setMapKey] = useState<string>(`map-${Date.now()}`);
  const mapContainerId = useRef(`map-container-${Math.random().toString(36).substring(2, 15)}`);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  // Process reviews to use display coordinates when available
  const processedReviews = useMemo(() => 
    reviews.map(review => ({
      ...review,
      coordinates: review.display_coordinates || review.coordinates
    })), [reviews]
  );

  // Initialize map
  useEffect(() => {
    setIsLoading(true);
    
    try {
      setIsMapReady(true);
    } catch (err) {
      console.error("Failed to load map:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update center position based on search query or first review
  useEffect(() => {
    if (searchQuery && processedReviews.length > 0) {
      const filteredBySearch = processedReviews.filter((review) =>
        (review.display_address || review.address_detail || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      if (filteredBySearch.length > 0) {
        const newCenter: google.maps.LatLngLiteral = {
          lat: filteredBySearch[0].coordinates.lat,
          lng: filteredBySearch[0].coordinates.lng,
        };
        setCenter(newCenter);
      }
    } else if (processedReviews.length > 0) {
      const validReview = processedReviews.find(
        (review) => review.coordinates && review.coordinates.lat && review.coordinates.lng
      );
      
      if (validReview) {
        const newCenter: google.maps.LatLngLiteral = {
          lat: validReview.coordinates.lat,
          lng: validReview.coordinates.lng,
        };
        setCenter(newCenter);
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(newCenter);
        }
      }
    }
  }, [searchQuery, processedReviews]);

  // Force re-render of map when reviews change or grouped reviews change
  useEffect(() => {
    if (isMapReady) {
      setMapKey(`map-${Date.now()}`);
    }
  }, [processedReviews.length, groupedReviews?.length, isMapReady]);
  
  if (!processedReviews.length) {
    return (
      <div
        className="flex items-center justify-center bg-muted rounded-lg"
        style={{ height }}
      >
        <p className="text-muted-foreground">無評價資料</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-muted rounded-lg"
        style={{ height }}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">載入地圖中...</p>
        </div>
      </div>
    );
  }

  const setMapRef = (map: google.maps.Map | null) => {
    if (map && !mapInstanceRef.current) {
      mapInstanceRef.current = map;
    }
  };

  return (
    <div className="w-full h-full">
      <div 
        className="rounded-lg overflow-hidden relative border border-border shadow-sm h-full"
        style={{ height, zIndex: 1 }}
        id={mapContainerId.current}
      >
        {isMapReady && (
          <MapContent 
            reviews={processedReviews} 
            center={center}
            mapKey={mapKey}
            groupedReviews={groupedReviews}
            setMapInstanceRef={setMapRef}
          />
        )}
      </div>
    </div>
  );
};

export default ReviewMap;
